import { AdministrationFeesValidation } from "utils/validators/AdministrationFeesValidation";
import { AmountValidation } from "utils/validators/AmountValidation";
import { LenderMonthlyInterestValidation } from "utils/validators/LenderMonthlyInterestValidation";
import { SetupFeeValidation } from "utils/validators/SetupFeeValidation";
import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum } from "@/features/common/enums";
import type { IRevolvingCreditBidInput } from "@/features/lender-responses/types/lender-response";

export const revolvingCreditBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<IRevolvingCreditBidInput> = (
  currency,
  formatWithCurrency,
) =>
  yup
    .object()
    .shape({
      type: yup
        .string()
        .required("Type is required")
        .oneOf([BidTypeEnum.REVOLVING_CREDIT])
        .default(BidTypeEnum.REVOLVING_CREDIT),
      amount: AmountValidation(currency, formatWithCurrency),
      monthlyAmortisation: yup
        .number()
        .typeError("Monthly amortisation is required")
        .required("Monthly amortisation is required")
        .min(
          0,
          (schema) => `Monthly amortisation can't be lower than ${schema.min}%`,
        )
        .max(
          100,
          (schema) =>
            `Monthly amortisation can't be greater than ${schema.max}%`,
        ),
      lenderMonthlyInterest: LenderMonthlyInterestValidation({
        isFunction: () => true,
      }).required(),
      withdrawalFee: yup
        .number()
        .nullable()
        .optional()
        .min(0, "Must be >= 0")
        .max(100, "Must be <= 100"),
      withdrawalFeeAmount: yup
        .number()
        .nullable()
        .optional()
        .min(0, "Must be >= 0"),
      setupFee: SetupFeeValidation(formatWithCurrency),
      limitFeePerYear: yup.number().optional().nullable(),
      administrationFees: AdministrationFeesValidation(formatWithCurrency, {
        boundByApprovedAmount: true,
      }),
    })
    .test(
      "withdrawal-exactly-one",
      "Exactly one of withdrawal fee (%) or withdrawal amount must be provided",
      function (values) {
        const hasFee = values?.withdrawalFee != null;
        const hasAmount = values?.withdrawalFeeAmount != null;
        if (hasFee === hasAmount) {
          const field = !hasFee ? "withdrawalFee" : "withdrawalFeeAmount";
          return this.createError({
            // createError takes the path as-is, so it must be prefixed with
            // this bid's own path (bids[i]) to reach the field's error slot.
            path: this.path ? `${this.path}.${field}` : field,
            message: hasFee
              ? "Provide only one: either withdrawal fee (%) or withdrawal amount"
              : "Provide either a withdrawal fee (%) or a withdrawal amount",
          });
        }
        return true;
      },
    );

export const revolvingCreditBidDefaultValues: IRevolvingCreditBidInput = {
  type: BidTypeEnum.REVOLVING_CREDIT,
  amount: undefined,
  monthlyAmortisation: undefined,
  lenderMonthlyInterest: undefined,
  withdrawalFee: undefined,
  withdrawalFeeAmount: undefined,
  setupFee: undefined,
  limitFeePerYear: undefined,
  administrationFees: undefined,
};
